import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ChevronDown, Star, Search, MapPin, X, KeyRound, LogOut, Compass } from "lucide-react";
import { navFor, hrefFor, locationHref, allItemsFor } from "./navConfig";

const slug = (s) => s.replace(/\W+/g, "-").replace(/^-|-$/g, "").toLowerCase() || "financials";

function isActive(pathname, to) {
  return to === "/" ? pathname === "/" : pathname === to || pathname.startsWith(`${to}/`);
}

function NavItem({ item, location, pathname, pinned, onPin, onNavigate }) {
  const active = isActive(pathname, item.to);
  return (
    <div className="group relative">
      <Link to={hrefFor(item, location)} onClick={onNavigate} data-testid={`nav-item-${slug(item.to)}`}
        className={`flex items-start gap-3 rounded-lg pl-3 pr-8 py-2 transition-colors duration-150 border-l-2 ${active ? "bg-brand-hover border-brand-ink text-brand-ink" : "border-transparent text-brand-muted hover:bg-brand-surface hover:text-brand-ink"}`}>
        <item.icon className="h-4 w-4 mt-0.5 shrink-0" />
        <span className="min-w-0">
          <span className={`flex items-center gap-1 text-sm leading-tight ${active ? "font-medium" : ""}`}>{item.label}{item.loc && location && <MapPin className="h-3 w-3 text-brand-tertiary shrink-0" data-testid={`nav-loc-mark-${slug(item.to)}`} />}</span>
          <span className="block text-[11px] leading-snug text-brand-tertiary mt-0.5 truncate">{item.desc}</span>
        </span>
      </Link>
      <button onClick={() => onPin(item.to)} aria-label={pinned ? "Unpin" : "Pin"} data-testid={`nav-pin-${slug(item.to)}`}
        className={`absolute right-2 top-2.5 p-0.5 rounded transition-opacity duration-150 ${pinned ? "opacity-100 text-brand-gold" : "opacity-0 group-hover:opacity-100 text-brand-tertiary hover:text-brand-ink"}`}>
        <Star className={`h-3.5 w-3.5 ${pinned ? "fill-current" : ""}`} />
      </button>
    </div>
  );
}

function LocationSwitcher({ locations, location, setLocation, onNavigate }) {
  if (!locations.length) return null;
  return (
    <div className="space-y-2" data-testid="nav-location-switcher">
      <p className="px-1 text-[10px] uppercase tracking-[0.15em] text-brand-tertiary">Viewing</p>
      <div className="relative">
        <MapPin className="h-3.5 w-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-brand-tertiary pointer-events-none" />
        <select value={location} onChange={(e) => setLocation(e.target.value)} data-testid="nav-location-select"
          className="w-full appearance-none bg-white border border-brand-border rounded-lg pl-8 pr-8 py-2 text-sm text-brand-ink focus:outline-none focus:border-brand-ink transition-colors cursor-pointer">
          <option value="">All locations</option>
          {locations.map((l) => <option key={l} value={l}>{l}</option>)}
        </select>
        {location
          ? <button onClick={() => setLocation("")} aria-label="Show all locations" data-testid="nav-location-clear" className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 text-brand-tertiary hover:text-brand-ink"><X className="h-3.5 w-3.5" /></button>
          : <ChevronDown className="h-3.5 w-3.5 absolute right-3 top-1/2 -translate-y-1/2 text-brand-tertiary pointer-events-none" />}
      </div>
      {location && (
        <Link to={locationHref(location)} onClick={onNavigate} data-testid="nav-location-overview"
          className="block text-xs text-brand-ink bg-brand-surface border border-brand-border rounded-lg px-3 py-2 hover:border-brand-ink transition-colors">
          Open {location} overview →
          <span className="block text-[11px] text-brand-tertiary mt-0.5">Pages with a map pin below open filtered to {location}</span>
        </Link>
      )}
    </div>
  );
}

function Group({ g, children, open, toggle }) {
  if (!g.label) return <div className="space-y-0.5">{children}</div>;
  return (
    <div data-testid={`nav-group-${g.key}`}>
      <button onClick={toggle} className="w-full flex items-center justify-between px-1 pt-4 pb-1.5 text-[10px] uppercase tracking-[0.15em] text-brand-tertiary hover:text-brand-ink transition-colors" data-testid={`nav-group-toggle-${g.key}`}>
        {g.label} <ChevronDown className={`h-3 w-3 transition-transform duration-200 ${open ? "" : "-rotate-90"}`} />
      </button>
      {open && <div className="space-y-0.5">{children}</div>}
    </div>
  );
}

export function SidebarContent({ user, locations, location, setLocation, pins, togglePin, onSearch, onNavigate, onPassword, onLogout, authDisabled }) {
  const { pathname } = useLocation();
  const groups = navFor(user?.role);
  const [closed, setClosed] = useState(() => { try { return JSON.parse(localStorage.getItem("bond.nav.closed") || "[]"); } catch { return []; } });
  const toggle = (k) => setClosed((c) => { const n = c.includes(k) ? c.filter((x) => x !== k) : [...c, k]; localStorage.setItem("bond.nav.closed", JSON.stringify(n)); return n; });
  const pinnedItems = allItemsFor(user?.role).filter((i) => pins.includes(i.to));
  const itemProps = { location, pathname, onPin: togglePin, onNavigate };
  return (
    <div className="flex flex-col h-full" data-testid="nav-sidebar">
      <div className="px-5 pt-5 pb-4 space-y-4 border-b border-brand-border shrink-0">
        <Link to="/home" onClick={onNavigate} className="block font-serif text-xl tracking-tight text-brand-ink" data-testid="nav-brand">Bond Collective</Link>
        <button onClick={onSearch} data-testid="nav-cmd-k-trigger"
          className="w-full flex items-center gap-2 bg-white border border-brand-border rounded-lg px-3 py-2 text-sm text-brand-tertiary hover:border-brand-ink transition-colors">
          <Search className="h-3.5 w-3.5" /> Find a page…
          <kbd className="ml-auto text-[10px] border border-brand-border rounded px-1.5 py-0.5 font-sans">Ctrl K</kbd>
        </button>
        <LocationSwitcher locations={locations} location={location} setLocation={setLocation} onNavigate={onNavigate} />
      </div>
      <nav className="flex-1 min-h-0 overflow-y-auto px-3 py-3">
        {pinnedItems.length > 0 && (
          <div data-testid="nav-pinned-section" className="pb-2">
            <p className="px-1 pt-1 pb-1.5 text-[10px] uppercase tracking-[0.15em] text-brand-tertiary flex items-center gap-1"><Star className="h-3 w-3 fill-current text-brand-gold" /> Pinned</p>
            <div className="space-y-0.5">{pinnedItems.map((i) => <NavItem key={i.to} item={i} pinned {...itemProps} />)}</div>
          </div>
        )}
        {groups.map((g) => (
          <Group key={g.key} g={g} open={!closed.includes(g.key)} toggle={() => toggle(g.key)}>
            {g.items.map((i) => <NavItem key={i.to} item={i} pinned={pins.includes(i.to)} {...itemProps} />)}
          </Group>
        ))}
      </nav>
      {!authDisabled && (
        <div className="shrink-0 border-t border-brand-border px-3 py-3 flex items-center gap-1">
          <div className="flex-1 min-w-0 px-2">
            <p className="text-sm text-brand-ink truncate" data-testid="nav-user-name">{user?.name || user?.email}</p>
            <p className="text-[10px] uppercase tracking-[0.15em] text-brand-tertiary">{user?.role}</p>
          </div>
          <button onClick={() => { onNavigate?.(); window.dispatchEvent(new Event("start-tour")); }} title="Replay tour" data-testid="nav-replay-tour" className="p-2 rounded-lg text-brand-muted hover:bg-brand-hover hover:text-brand-ink transition-colors"><Compass className="h-4 w-4" /></button>
          <button onClick={onPassword} title="Change password" data-testid="menu-change-password" className="p-2 rounded-lg text-brand-muted hover:bg-brand-hover hover:text-brand-ink transition-colors"><KeyRound className="h-4 w-4" /></button>
          <button onClick={onLogout} title="Sign out" data-testid="logout-button" className="p-2 rounded-lg text-brand-muted hover:bg-brand-hover hover:text-brand-ink transition-colors"><LogOut className="h-4 w-4" /></button>
        </div>
      )}
    </div>
  );
}
